// src/lib/kinema/core/tools/snapCalculation.ts
import type { Rect } from './types';

export interface GuideLine {
  orientation: 'horizontal' | 'vertical';
  position: number;
}

export interface SnapResult {
  snappedX?: number;
  snappedY?: number;
  guides: GuideLine[];
}

export function calculateSnap(
  moving: Rect,
  others: Rect[],
  threshold: number,
): SnapResult {
  let snappedX: number | undefined;
  let snappedY: number | undefined;
  let bestDx = threshold + 1;
  let bestDy = threshold + 1;
  let guideXPosition: number | undefined; // the actual edge coordinate to draw the guide at
  let guideYPosition: number | undefined;

  const movingCenterX = moving.x + moving.width / 2;
  const movingCenterY = moving.y + moving.height / 2;
  const movingRight = moving.x + moving.width;
  const movingBottom = moving.y + moving.height;

  for (const other of others) {
    const otherCenterX = other.x + other.width / 2;
    const otherCenterY = other.y + other.height / 2;
    const otherRight = other.x + other.width;
    const otherBottom = other.y + other.height;

    // X-axis: [movingEdge, otherEdge, guideAt]
    const xEdges: [number, number, number][] = [
      [moving.x, other.x, other.x],                 // left to left → guide at left edge
      [movingRight, otherRight, otherRight],          // right to right → guide at right edge
      [movingCenterX, otherCenterX, otherCenterX],    // center to center → guide at center
      [moving.x, otherRight, otherRight],             // left to right → guide at other's right
      [movingRight, other.x, other.x],               // right to left → guide at other's left
    ];

    for (const [movingEdge, otherEdge, guideAt] of xEdges) {
      const dx = Math.abs(movingEdge - otherEdge);
      if (dx <= threshold && dx < bestDx) {
        bestDx = dx;
        snappedX = moving.x + (otherEdge - movingEdge);
        guideXPosition = guideAt;
      }
    }

    // Y-axis: [movingEdge, otherEdge, guideAt]
    const yEdges: [number, number, number][] = [
      [moving.y, other.y, other.y],
      [movingBottom, otherBottom, otherBottom],
      [movingCenterY, otherCenterY, otherCenterY],
      [moving.y, otherBottom, otherBottom],
      [movingBottom, other.y, other.y],
    ];

    for (const [movingEdge, otherEdge, guideAt] of yEdges) {
      const dy = Math.abs(movingEdge - otherEdge);
      if (dy <= threshold && dy < bestDy) {
        bestDy = dy;
        snappedY = moving.y + (otherEdge - movingEdge);
        guideYPosition = guideAt;
      }
    }
  }

  // Build guide lines at the exact alignment position
  const guides: GuideLine[] = [];
  if (snappedX !== undefined && guideXPosition !== undefined) {
    guides.push({ orientation: 'vertical', position: guideXPosition });
  }
  if (snappedY !== undefined && guideYPosition !== undefined) {
    guides.push({ orientation: 'horizontal', position: guideYPosition });
  }

  return { snappedX, snappedY, guides };
}
